import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { holidayAPI } from '../services/api';
import { formatDateIST } from '../utils/dateFormatter';
import Swal from 'sweetalert2';
import { FaCalendarAlt, FaPlus, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Holidays = () => {
  const { isAdmin, isHR } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: '',
    is_mandatory: true,
    holiday_type: 'public' // 'public' or 'restricted'
  });

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const response = await holidayAPI.getAll(new Date().getFullYear());
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await holidayAPI.create(formData);
      setShowModal(false);
      setFormData({ name: '', date: '', description: '', is_mandatory: true, holiday_type: 'public' });
      fetchHolidays();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Holiday added successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error adding holiday',
        confirmButtonColor: '#635bff'
      });
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const isHoliday = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const dateStr = new Date(year, month, day).toISOString().split('T')[0];
    return holidays.find(h => h.date.split('T')[0] === dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() &&
           currentDate.getMonth() === today.getMonth() &&
           currentDate.getFullYear() === today.getFullYear();
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Day headers
    const headers = dayNames.map(name => (
      <div key={name} style={{
        padding: '12px',
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: '600',
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        {name}
      </div>
    ));

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={{ padding: '12px' }}></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const holiday = isHoliday(day);
      const today = isToday(day);

      days.push(
        <div key={day} style={{
          padding: '8px',
          textAlign: 'center',
          position: 'relative'
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            backgroundColor: holiday ? (holiday.is_mandatory ? '#e8e5ff' : '#d1fae5') : (today ? '#fef3c7' : 'transparent'),
            color: holiday ? (holiday.is_mandatory ? '#635bff' : '#065f46') : (today ? '#92400e' : '#374151'),
            border: today ? '2px solid #f59e0b' : 'none',
            cursor: holiday ? 'pointer' : 'default',
            position: 'relative'
          }}
          title={holiday ? holiday.name : ''}>
            {day}
            {holiday && (
              <div style={{
                position: 'absolute',
                bottom: '2px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: holiday.is_mandatory ? '#635bff' : '#10b981'
              }}></div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '4px'
      }}>
        {headers}
        {days}
      </div>
    );
  };

  const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#0a0a0a',
          letterSpacing: '-0.02em',
          margin: 0
        }}>
          Holiday Calendar
        </h1>
        {(isAdmin || isHR) && (
          <button
            onClick={() => setShowModal(true)}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              backgroundColor: '#635bff',
              color: 'white',
              border: 'none',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            <FaPlus size={14} />
            Add Holiday
          </button>
        )}
      </div>

      {/* Holiday List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#faf9ff'
        }}>
          <h5 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: '#0a0a0a'
          }}>
            All Holidays
          </h5>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table hover responsive style={{ margin: 0 }}>
            <thead style={{ backgroundColor: '#faf9ff' }}>
              <tr>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '25%'
                }}>Holiday Name</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '15%'
                }}>Date</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '10%'
                }}>Day</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '35%'
                }}>Description</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  width: '15%'
                }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {holidays.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{
                    padding: '60px 20px',
                    textAlign: 'center',
                    color: '#9ca3af',
                    fontSize: '14px'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                      No holidays
                    </div>
                    <div>Add holidays to see them here</div>
                  </td>
                </tr>
              ) : (
                holidays.map((holiday) => {
                  const holidayDate = new Date(holiday.date);
                  const dayName = holidayDate.toLocaleDateString('en-US', { weekday: 'short' });

                  return (
                    <tr key={holiday.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#0a0a0a'
                      }}>
                        {holiday.name}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {formatDateIST(holiday.date)}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '500'
                      }}>
                        {dayName}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {holiday.description || '-'}
                      </td>
                      <td style={{
                        padding: '16px 24px'
                      }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: holiday.is_mandatory ? '#e8e5ff' : '#d1fae5',
                          color: holiday.is_mandatory ? '#635bff' : '#065f46'
                        }}>
                          {holiday.is_mandatory ? 'Mandatory' : 'Optional'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Calendar Snippet */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        marginTop: '24px',
        marginBottom: '24px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#faf9ff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaCalendarAlt style={{ color: '#635bff', fontSize: '20px' }} />
            <h5 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#0a0a0a'
            }}>
              {monthYear}
            </h5>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => changeMonth(-1)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#635bff',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <FaChevronLeft />
            </button>
            <button
              onClick={() => changeMonth(1)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#635bff',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
        <div style={{ padding: '16px 24px' }}>
          {renderCalendar()}

          {/* Legend */}
          <div style={{
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#635bff'
              }}></div>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Public Holiday</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#10b981'
              }}></div>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Restricted Holiday</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                border: '2px solid #f59e0b',
                backgroundColor: '#fef3c7'
              }}></div>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Today</span>
            </div>
          </div>
        </div>
      </div>

      

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb' }}>
          <Modal.Title style={{ fontSize: '20px', fontWeight: '600', color: '#0a0a0a' }}>
            Add Holiday
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Holiday Name
              </Form.Label>
              <Form.Control
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  padding: '10px 12px',
                  fontSize: '14px'
                }}
                placeholder="e.g., Diwali, Christmas"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Date
              </Form.Label>
              <Form.Control
                required
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  padding: '10px 12px',
                  fontSize: '14px'
                }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Description (Optional)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  padding: '10px 12px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}
                placeholder="Add a description..."
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', display: 'block' }}>
                Holiday Type
              </Form.Label>
              <div style={{
                backgroundColor: '#f9fafb',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <Form.Check
                  type="radio"
                  id="public-holiday"
                  name="holiday_type"
                  label={
                    <div>
                      <div style={{ fontWeight: '500', color: '#0a0a0a', marginBottom: '4px' }}>Public Holiday</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>Mandatory for all employees</div>
                    </div>
                  }
                  checked={formData.holiday_type === 'public'}
                  onChange={() => setFormData({ ...formData, holiday_type: 'public', is_mandatory: true })}
                  style={{ marginBottom: '12px' }}
                />
                <Form.Check
                  type="radio"
                  id="restricted-holiday"
                  name="holiday_type"
                  label={
                    <div>
                      <div style={{ fontWeight: '500', color: '#0a0a0a', marginBottom: '4px' }}>Restricted Holiday</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>Optional for employees to choose</div>
                    </div>
                  }
                  checked={formData.holiday_type === 'restricted'}
                  onChange={() => setFormData({ ...formData, holiday_type: 'restricted', is_mandatory: false })}
                />
              </div>
            </Form.Group>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: '#635bff',
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Add Holiday
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Holidays;
